import {
    FeedbackAction,
    IInsightAction,
    InsightActionStatus
} from "./coachInsightsModels";

const STORAGE_KEY = "coachInsights_actions_v1";

function loadAll(): IInsightAction[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? (JSON.parse(raw) as IInsightAction[]) : [];
    } catch {
        return [];
    }
}

function saveAll(actions: IInsightAction[]): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(actions));
}

const insightActionsStore = {
    async listActions(): Promise<IInsightAction[]> {
        return loadAll();
    },

    async getActionForInsight(insightKey: string): Promise<IInsightAction | null> {
        const all = loadAll();
        return all.find((a) => a.insightKey === insightKey) ?? null;
    },

    async recordAction(
        insight: Omit<
            IInsightAction,
            | "id"
            | "createdAt"
            | "status"
            | "reviewedAt"
            | "reviewOutcome"
            | "actionType"
            | "note"
        >,
        actionType: FeedbackAction,
        note: string = ""
    ): Promise<IInsightAction> {
        const all = loadAll().filter((a) => a.insightKey !== insight.insightKey);
        const record: IInsightAction = {
            ...insight,
            actionType,
            note,
            id: `${insight.insightKey}|${Date.now()}`,
            createdAt: new Date().toISOString(),
            status: InsightActionStatus.Open,
            reviewedAt: null,
            reviewOutcome: null
        };
        saveAll([...all, record]);
        return record;
    },

    async dismissAction(insightKey: string): Promise<void> {
        const all = loadAll().filter((a) => a.insightKey !== insightKey);
        saveAll(all);
    },

    async archiveAction(insightKey: string): Promise<void> {
        const all = loadAll().filter((a) => a.insightKey !== insightKey);
        saveAll(all);
    },

    async restoreAction(action: IInsightAction): Promise<void> {
        const all = loadAll().filter(
            (item) => item.insightKey !== action.insightKey
        );
        saveAll([...all, action]);
    },

    async reassignAction(
        insightKey: string,
        assigneeName: string
    ): Promise<IInsightAction | null> {
        const all = loadAll();
        const idx = all.findIndex((a) => a.insightKey === insightKey);
        if (idx === -1) return null;
        all[idx] = {
            ...all[idx],
            whoToFollowUpWith: [assigneeName]
        };
        saveAll(all);
        return all[idx];
    },

    async selectActionPlay(
        insightKey: string,
        selectedActionId: string
    ): Promise<IInsightAction | null> {
        const all = loadAll();
        const idx = all.findIndex((a) => a.insightKey === insightKey);
        if (idx === -1) return null;
        all[idx] = {
            ...all[idx],
            selectedActionId
        };
        saveAll(all);
        return all[idx];
    },

    async setStatus(
        insightKey: string,
        status: InsightActionStatus
    ): Promise<IInsightAction | null> {
        const all = loadAll();
        const idx = all.findIndex((a) => a.insightKey === insightKey);
        if (idx === -1) return null;
        const isTerminal =
            status === InsightActionStatus.Resolved ||
            status === InsightActionStatus.NotImproved;
        all[idx] = {
            ...all[idx],
            status,
            reviewedAt: isTerminal
                ? all[idx].reviewedAt ?? new Date().toISOString()
                : null,
            reviewOutcome: isTerminal ? all[idx].reviewOutcome : null
        };
        saveAll(all);
        return all[idx];
    },

    async updateActionStatus(
        insightKey: string,
        status: InsightActionStatus,
        reviewOutcome: IInsightAction["reviewOutcome"] = null
    ): Promise<IInsightAction | null> {
        const all = loadAll();
        const idx = all.findIndex((a) => a.insightKey === insightKey);
        if (idx === -1) return null;
        all[idx] = {
            ...all[idx],
            status,
            reviewOutcome,
            reviewedAt: new Date().toISOString()
        };
        saveAll(all);
        return all[idx];
    }
};

export default insightActionsStore;
